import {
  ApplyStoreSettingsToEvaluationPart,
  AutoCalculatePrices,
  BulkUpdatePartStoreSettings,
  CreateInspectionOrder,
  InspectionOrder,
  Route,
  ServiceBikeBrakeSystem,
  ServiceBikeGearSystem,
  ServiceBikePartWithStoreSettings,
  UpdateInspectionOrder,
  populateRoute,
} from "@repo/common"
import {
  QueryClient,
  UseSuspenseQueryOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { request } from "../api"

interface PartFilter {
  storeId: string
  priceListId?: string
  isElectric?: boolean
  brakeSystemFront?: ServiceBikeBrakeSystem
  brakeSystemBack?: ServiceBikeBrakeSystem
  gearSystem?: ServiceBikeGearSystem
  bikeHasFrontGears?: boolean
  bikeHasDamperFront?: boolean
  bikeHasDamperBack?: boolean
  exclude?: string[]
  include?: string[]
}

async function fetchParts(
  filters: PartFilter,
): Promise<ServiceBikePartWithStoreSettings[]> {
  const { storeId, ...params } = filters

  return await request<ServiceBikePartWithStoreSettings[]>({
    url: populateRoute(
      Route.PART_EVALUATION_CONTROLLER__FIND_MANY_WITH_STORE_SETTINGS,
      storeId,
    ),
    params,
    method: "GET",
  })
}

async function fetchPart(storeId: string, id: string) {
  return await request<ServiceBikePartWithStoreSettings>({
    url: populateRoute(
      Route.PART_EVALUATION_CONTROLLER__FIND_ONE_WITH_STORE_SETTINGS,
      storeId,
      id,
    ),
    method: "GET",
  })
}

async function updateStoreSettings(
  data: ApplyStoreSettingsToEvaluationPart,
  storeId: string,
): Promise<void> {
  return await request<void>({
    url: populateRoute(
      Route.PART_EVALUATION_CONTROLLER__APPLY_STORE_SETTINGS,
      storeId,
    ),
    method: "PATCH",
    data,
  })
}

async function fetchInspectionOrders(
  storeId: string,
): Promise<InspectionOrder[]> {
  return await request<InspectionOrder[]>({
    url: populateRoute(
      Route.PART_EVALUATION_CONTROLLER__FIND_INSPECTION_ORDERS,
      storeId,
    ),
    method: "GET",
  })
}

async function createInspectionOrder(
  storeId: string,
  data: CreateInspectionOrder,
): Promise<{ id: string }> {
  return await request<{ id: string }>({
    url: populateRoute(
      Route.PART_EVALUATION_CONTROLLER__CREATE_INSPECTION_ORDER,
      storeId,
    ),
    method: "POST",
    data,
  })
}

async function updateInspectionOrder(
  storeId: string,
  inspectionOrderId: string,
  data: UpdateInspectionOrder,
): Promise<void> {
  return await request<void>({
    url: populateRoute(
      Route.PART_EVALUATION_CONTROLLER__UPDATE_INSPECTION_ORDER,
      storeId,
      inspectionOrderId,
    ),
    method: "PATCH",
    data,
  })
}

async function deleteInspectionOrder(
  storeId: string,
  inspectionOrderId: string,
): Promise<void> {
  return await request<void>({
    url: populateRoute(
      Route.PART_EVALUATION_CONTROLLER__DELETE_INSPECTION_ORDER,
      storeId,
      inspectionOrderId,
    ),
    method: "DELETE",
  })
}

async function bulkUpdateStoreSettings(
  storeId: string,
  data: BulkUpdatePartStoreSettings,
): Promise<void> {
  return await request<void>({
    url: populateRoute(
      Route.PART_EVALUATION_CONTROLLER__BULK_UPDATE_STORE_SETTINGS,
      storeId,
    ),
    method: "PATCH",
    data,
  })
}

async function autoCalculatePrices(
  storeId: string,
  data: AutoCalculatePrices,
): Promise<void> {
  return await request<void>({
    url: populateRoute(
      Route.PART_EVALUATION_CONTROLLER__AUTO_CALCULATE_PRICES,
      storeId,
    ),
    method: "POST",
    data,
  })
}

interface PartWithDeactivationStatus {
  id: string
  name: string
  image: string
  isDeactivated: boolean
}

async function fetchPartsWithDeactivationStatus(
  storeId: string,
): Promise<PartWithDeactivationStatus[]> {
  return await request<PartWithDeactivationStatus[]>({
    url: populateRoute(
      Route.PART_EVALUATION_CONTROLLER__GET_DEACTIVATED_PARTS,
      storeId,
    ),
    method: "GET",
  })
}

async function deactivatePart(storeId: string, partId: string): Promise<void> {
  return await request<void>({
    url: populateRoute(
      Route.PART_EVALUATION_CONTROLLER__DEACTIVATE_PART,
      storeId,
      partId,
    ),
    method: "POST",
  })
}

async function activatePart(storeId: string, partId: string): Promise<void> {
  return await request<void>({
    url: populateRoute(
      Route.PART_EVALUATION_CONTROLLER__ACTIVATE_PART,
      storeId,
      partId,
    ),
    method: "DELETE",
  })
}

export function useUpdateStoreSettings(storeId: string) {
  const queryClient = useQueryClient()

  const invalidateQueries = async () =>
    queryClient.invalidateQueries(partQueries.list({ storeId }))

  return useMutation({
    mutationFn: (data: ApplyStoreSettingsToEvaluationPart) =>
      updateStoreSettings(data, storeId),
    onSettled: invalidateQueries,
  })
}

function invalidateInspectionOrderQueries(
  queryClient: QueryClient,
  storeId: string,
) {
  // The store's first order drives the parts' sortPriority, so part lists
  // and reception data are affected too
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: partQueries.inspectionOrders(storeId).queryKey,
    }),
    queryClient.invalidateQueries({ queryKey: partQueries.lists() }),
    queryClient.invalidateQueries({
      queryKey: ["reception-data-booking", storeId],
    }),
  ])
}

export function useCreateInspectionOrder(storeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateInspectionOrder) =>
      createInspectionOrder(storeId, data),
    onSettled: () => invalidateInspectionOrderQueries(queryClient, storeId),
  })
}

export function useUpdateInspectionOrder(storeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      inspectionOrderId,
      data,
    }: {
      inspectionOrderId: string
      data: UpdateInspectionOrder
    }) => updateInspectionOrder(storeId, inspectionOrderId, data),
    // Every save replaces the whole part list, so quick consecutive changes
    // must reach the API one at a time for the last one to win
    scope: { id: `inspection-order-${storeId}` },
    onSettled: () => invalidateInspectionOrderQueries(queryClient, storeId),
  })
}

export function useDeleteInspectionOrder(storeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (inspectionOrderId: string) =>
      deleteInspectionOrder(storeId, inspectionOrderId),
    onSettled: () => invalidateInspectionOrderQueries(queryClient, storeId),
  })
}

export function useBulkUpdateStoreSettings(storeId: string) {
  const queryClient = useQueryClient()
  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: partQueries.lists() })
  }
  return useMutation({
    mutationFn: (data: BulkUpdatePartStoreSettings) =>
      bulkUpdateStoreSettings(storeId, data),
    onSettled: invalidate,
  })
}

export function useAutoCalculatePrices(storeId: string) {
  const queryClient = useQueryClient()
  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: partQueries.lists() })
  }
  return useMutation({
    mutationFn: (data: AutoCalculatePrices) =>
      autoCalculatePrices(storeId, data),
    onSettled: invalidate,
  })
}

function invalidatePartQueries(queryClient: QueryClient, storeId: string) {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: partQueries.partsWithDeactivationStatus(storeId).queryKey,
    }),
    queryClient.invalidateQueries({ queryKey: partQueries.lists() }),
  ])
}

export function useDeactivatePart(storeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (partId: string) => deactivatePart(storeId, partId),
    onSettled: () => invalidatePartQueries(queryClient, storeId),
  })
}

export function useActivatePart(storeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (partId: string) => activatePart(storeId, partId),
    onSettled: () => invalidatePartQueries(queryClient, storeId),
  })
}

const partQueries = {
  all: () => ["parts"],
  lists: () => [...partQueries.all(), "list"],
  list: <TData = ServiceBikePartWithStoreSettings[]>(
    filters: PartFilter,
    options?: Omit<
      UseSuspenseQueryOptions<ServiceBikePartWithStoreSettings[], TData>,
      "queryKey" | "queryFn"
    >,
  ) =>
    queryOptions({
      ...options,
      queryKey: [...partQueries.lists(), filters],
      queryFn: () => fetchParts(filters),
    }),
  details: () => [...partQueries.all(), "detail"],
  detail: (storeId: string, id: string) =>
    queryOptions({
      queryKey: [...partQueries.details(), ...storeId, id],
      queryFn: () => fetchPart(storeId, id),
    }),
  partsWithDeactivationStatus: (storeId: string) =>
    queryOptions({
      queryKey: [...partQueries.all(), "deactivation-status", storeId],
      queryFn: () => fetchPartsWithDeactivationStatus(storeId),
    }),
  inspectionOrders: (storeId: string) =>
    queryOptions({
      queryKey: [...partQueries.all(), "inspection-orders", storeId],
      queryFn: () => fetchInspectionOrders(storeId),
    }),
}

export default partQueries
